import type { CSSProperties, ReactNode } from 'react';
import type { GameId, RoleId } from '@ringmaster/shared';
import { roleCardDefFor } from './roleCardDefs.js';

/**
 * RolePortrait — THE way a role is pictured anywhere outside a full card: the
 * role's character art, cover-cropped from the top (faces, not chests) into a
 * ringed circle on the arcana-ink disc.
 *
 * Extracted because the same recipe was already living in three places and
 * would have grown a fourth: {@link RoleTally} (WhisperRoleTally.tsx — the
 * pool/roster strip, with a ×N badge overlay), `RoleExplorer` (HowToPlay.tsx —
 * the tappable how-to-play row) and the role guide inside
 * {@link OneNightRolesButton}. Callers own only the things that legitimately
 * differ: `size`, the `ring` border and its `glow`, plus any overlay
 * (`children`, e.g. the count badge) — never the art resolution or the crop, so
 * a role always wears the same face at every size.
 *
 * `gameId` resolves per-game art via {@link roleCardDefFor} (One Night's Thief
 * and Joker have their own renders; The Whisper has its own Deceiver/Paladin).
 */
export function RolePortrait({
  gameId,
  role,
  size,
  ring,
  glow = 'none',
  style,
  children,
}: {
  gameId: GameId | undefined;
  role: RoleId;
  size: number;
  /** Full CSS `border` shorthand — width and colour both vary by caller. */
  ring: string;
  /** CSS `box-shadow` for the accent glow. */
  glow?: string;
  style?: CSSProperties;
  /** Overlay pinned to the portrait — the strip's ×N count badge. */
  children?: ReactNode;
}) {
  const def = roleCardDefFor(gameId, role);
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        // Visible, not hidden: the count badge deliberately hangs off the disc.
        overflow: 'visible',
        border: ring,
        boxShadow: glow,
        background: 'rgba(22,11,38,0.6)',
        ...style,
      }}
    >
      <img
        src={def.character}
        alt=""
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          objectFit: 'cover',
          objectPosition: 'top',
        }}
      />
      {children}
    </span>
  );
}
